import { useState } from 'react';
import { Plus, X } from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import { CineCombo } from '../CineCombo';
import { SCENE_KEYS, SceneSetupFields } from '../SceneSetupFields';
import { cn } from '../../../lib/utils';

const Label = ({ children }) => <div className="mb-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-neutral-400">{children}</div>;
export const storyDraft = (sc) => ({
  heading: sc.heading || '', summary: sc.summary || '', location: sc.location || '', characters: sc.characters || [],
  ...Object.fromEntries(SCENE_KEYS.map((k) => [k, sc[k] || ''])),
});

function CharacterChips({ kit, value, onChange, testPrefix }) {
  const [typed, setTyped] = useState('');
  const names = [...new Set([...kit.filter((a) => a.kind === 'character').map((a) => a.name), ...value])];
  const toggle = (n) => onChange(value.includes(n) ? value.filter((x) => x !== n) : [...value, n].slice(0, 6));
  const add = () => { const n = typed.trim(); if (n && !value.includes(n)) onChange([...value, n].slice(0, 6)); setTyped(''); };
  return (
    <div className="flex flex-wrap items-center gap-1.5" data-testid={`${testPrefix}-characters`}>
      {names.map((n) => (
        <button key={n} type="button" onClick={() => toggle(n)} data-testid={`${testPrefix}-char-${n.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
          className={cn('rounded-full border px-2.5 py-1 text-[11px] transition-colors duration-200',
            value.includes(n) ? 'border-amber-400 bg-amber-500/15 text-amber-100' : 'border-neutral-700 text-neutral-300 hover:border-neutral-500')}>{n}</button>
      ))}
      <span className="flex items-center gap-1">
        <Input value={typed} onChange={(e) => setTyped(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && add()} placeholder="New character"
          className="h-7 w-32 border-neutral-700 bg-neutral-950 text-[11px]" data-testid={`${testPrefix}-char-input`} />
        <button type="button" onClick={add} className="text-neutral-300 hover:text-amber-200" data-testid={`${testPrefix}-char-add`}><Plus size={13} /></button>
      </span>
    </div>
  );
}

/** Scene-by-scene editor for one story scene — what happens, where, who, and the full professional setup. */
export function StorySceneForm({ project, catalog, draft, setDraft, onSave, onCancel, saving, testPrefix }) {
  const kit = project.show_kit || [];
  const set = (patch) => setDraft((d) => ({ ...d, ...patch }));
  const places = kit.filter((a) => a.kind === 'location').map((a) => a.name);
  return (
    <div className="space-y-4 border-t border-neutral-800 pt-4" data-testid={`${testPrefix}-form`}>
      <div className="grid gap-3 md:grid-cols-[1fr_260px]">
        <div><Label>Scene heading</Label>
          <Input value={draft.heading} onChange={(e) => set({ heading: e.target.value })} className="border-neutral-700 bg-neutral-950 text-sm" data-testid={`${testPrefix}-heading`} /></div>
        <CineCombo label="Location" value={draft.location} options={places} allowCustom onChange={(v) => set({ location: v })} placeholder="Pick or type a place"
          clearLabel="No fixed location" testId={`${testPrefix}-location`} />
      </div>
      <div><Label>What happens</Label>
        <Textarea rows={3} value={draft.summary} onChange={(e) => set({ summary: e.target.value })} className="border-neutral-700 bg-neutral-950 text-sm"
          placeholder="Who wants what, what stands in the way, and how the scene turns." data-testid={`${testPrefix}-summary`} /></div>
      <div><Label>Who is in the scene</Label><CharacterChips kit={kit} value={draft.characters} onChange={(characters) => set({ characters })} testPrefix={testPrefix} /></div>
      <SceneSetupFields catalog={catalog} value={draft} onChange={set} testPrefix={testPrefix} />
      <div className="flex justify-end gap-2">
        <Button size="sm" variant="ghost" onClick={onCancel} className="text-neutral-300" data-testid={`${testPrefix}-cancel`}><X size={13} className="mr-1" /> Cancel</Button>
        <Button size="sm" disabled={saving} onClick={onSave} className="bg-amber-500 text-neutral-900 hover:bg-amber-400" data-testid={`${testPrefix}-save`}>{saving ? 'Saving…' : 'Save scene'}</Button>
      </div>
    </div>
  );
}
